import PropTypes from "prop-types";
import React, { useContext, useEffect, useRef, useState } from "react";

import { useIntersectionObserver, useResizeObserver } from "@swa-ui/browser";
import { classNames } from "@swa-ui/string";

import { ConfigurationContext } from "../ConfigurationContext";
import styles from "./Sticky.module.scss";

const STICKY_OFFSET = -1;
const STICKY_THRESHOLD = 1;
const STUCK = "stuck";
const UNSTUCK = "unstuck";

/**
 * Sticky provides a means to place a fixed element at the bottom, left, right or top of the page,
 * or an element that "sticks" to the page when the nearest scrolling ancestor is scrolled, typically
 * the viewport.
 */
export const Sticky = (props) => {
  const {
    children,
    className,
    direction = "top",
    offset,
    onStick,
    onUnstick,
    position = "sticky",
  } = props;
  const { registerStickyElement } = useContext(ConfigurationContext) ?? {};
  const [contentFullyVisible, setContentFullyVisible] = useState(true);
  const [stickyIndicatorFullyVisible, setStickyIndicatorFullyVisible] = useState(true);
  const previousStickyStateRef = useRef(UNSTUCK);
  const stickyRef = useRef();
  const contentRef = useRef();

  useEffect(() => {
    if (contentFullyVisible) {
      if (stickyIndicatorFullyVisible && previousStickyStateRef.current === STUCK) {
        previousStickyStateRef.current = UNSTUCK;
        onUnstick?.();
      } else if (!stickyIndicatorFullyVisible && previousStickyStateRef.current === UNSTUCK) {
        previousStickyStateRef.current = STUCK;
        onStick?.();
      }
    }
  }, [contentFullyVisible, stickyIndicatorFullyVisible]);

  useIntersectionObserver(stickyRef, handleStickyIndicatorIntersection, {
    condition: () => true,
    threshold: STICKY_THRESHOLD,
  });

  useIntersectionObserver(contentRef, handleContentIntersection, {
    condition: () => true,
    threshold: STICKY_THRESHOLD,
  });

  useResizeObserver({
    callback: handleStickyElementRegistration,
    element: contentRef,
  });

  return (
    <div {...getProps()}>
      <div ref={stickyRef} style={getStickyIndicatorStyling()} />
      <div ref={contentRef}>{children}</div>
    </div>
  );

  function getProps() {
    return {
      className: classNames(className, { [styles.onTop]: !stickyIndicatorFullyVisible }),
      style: getStyles(),
    };
  }

  function handleContentIntersection(eventDetails) {
    position === "sticky" && setContentFullyVisible(isFullyOverlapping(eventDetails));
  }

  function handleStickyElementRegistration() {
    position === "fixed" && registerStickyElement?.(contentRef.current, offset);
  }

  function handleStickyIndicatorIntersection(eventDetails) {
    position === "sticky" && setStickyIndicatorFullyVisible(isFullyOverlapping(eventDetails));
  }

  function getStyles() {
    return position === "fixed" ? { position, ...offset } : { [direction]: 0, position };
  }

  function getStickyIndicatorStyling() {
    const directions = {
      bottom: "50%",
      left: "50%",
      right: "50%",
      top: "50%",
    };

    directions[direction] = STICKY_OFFSET;

    if (direction === "bottom") {
      directions.top = "100%";
    } else if (direction === "left") {
      directions.right = "100%";
    } else if (direction === "right") {
      directions.left = "100%";
    } else if (direction === "top") {
      directions.bottom = "100%";
    }

    return {
      ...directions,
      height: 1,
      position: "absolute",
      width: 1,
    };
  }

  function isFullyOverlapping(eventDetails) {
    return eventDetails.intersectionRatio === STICKY_THRESHOLD;
  }
};

Sticky.displayName = "Sticky";

Sticky.propTypes = {
  /** Content that will be rendered. */
  children: PropTypes.node.isRequired,

  /**
   * Additional classes for positioning the component. Given classes may only position this component
   * for layout purposes, and cannot change how the component renders in any way.
   */
  className: PropTypes.string,

  /** When "sticky" positioned, which edge of the scrolling ancestor to "stick" to. */
  direction: PropTypes.oneOf(["bottom", "left", "right", "top"]),

  /** When "fixed" positioned, the offset value in pixels to position itself absolutely by. */
  offset: PropTypes.shape({
    bottom: PropTypes.number,
    left: PropTypes.number,
    right: PropTypes.number,
    top: PropTypes.number,
  }),

  /** Callback to fire when a sticky element "sticks" to the edge of the scrollable ancestor. */
  onStick: PropTypes.func,

  /**
   * Callback to fire when a sticky element is no longer "stuck" to the edge of the scrollable
   * ancestor.
   */
  onUnstick: PropTypes.func,

  /** Which CSS position attribute value to use. */
  position: PropTypes.oneOf(["fixed", "sticky"]),
};
